import { useMemo } from 'react';
import { TodoTree } from '../types/todo';
import { getItemById } from '../utils/tree';

export interface NodeProgress {
  completed: number;
  total: number;
  percentage: number;
  hasChildren: boolean;
}

/**
 * Calculates completion progress for a node and all its descendants
 */
export function useProgressCalculation(tree: TodoTree, nodeId: string): NodeProgress {
  return useMemo(() => {
    const item = getItemById(tree, nodeId);
    if (!item) {
      return { completed: 0, total: 0, percentage: 0, hasChildren: false };
    }

    // If it's a leaf node, return its own completion status
    if (item.children.length === 0) {
      return {
        completed: item.done ? 1 : 0,
        total: 1,
        percentage: item.done ? 100 : 0,
        hasChildren: false,
      };
    }

    // For non-leaf nodes, calculate descendant completion
    const descendantStats = calculateDescendantCompletion(tree, nodeId);

    return {
      ...descendantStats,
      hasChildren: true,
    };
  }, [tree, nodeId]);
}

/**
 * Recursively calculates completion statistics for all descendants of a node
 */
function calculateDescendantCompletion(tree: TodoTree, nodeId: string): Omit<NodeProgress, 'hasChildren'> {
  const item = getItemById(tree, nodeId);
  if (!item) {
    return { completed: 0, total: 0, percentage: 0 };
  }

  // If it's a leaf node, return its completion status
  if (item.children.length === 0) {
    return {
      completed: item.done ? 1 : 0,
      total: 1,
      percentage: item.done ? 100 : 0,
    };
  }

  // For non-leaf nodes, sum up children's completion
  let totalCompleted = 0;
  let totalNodes = 0;

  for (const childId of item.children) {
    const childStats = calculateDescendantCompletion(tree, childId);
    totalCompleted += childStats.completed;
    totalNodes += childStats.total;
  }

  return {
    completed: totalCompleted,
    total: totalNodes,
    percentage: totalNodes > 0 ? Math.round((totalCompleted / totalNodes) * 100) : 0,
  };
}

/**
 * Hook to get progress for multiple nodes at once
 */
export function useMultipleProgressCalculation(tree: TodoTree, nodeIds: string[]): Map<string, NodeProgress> {
  return useMemo(() => {
    const progressMap = new Map<string, NodeProgress>();

    nodeIds.forEach(nodeId => {
      const item = getItemById(tree, nodeId);
      if (!item) return;

      if (item.children.length === 0) {
        progressMap.set(nodeId, {
          completed: item.done ? 1 : 0,
          total: 1,
          percentage: item.done ? 100 : 0,
          hasChildren: false,
        });
      } else {
        const descendantStats = calculateDescendantCompletion(tree, nodeId);
        progressMap.set(nodeId, {
          ...descendantStats,
          hasChildren: true,
        });
      }
    });

    return progressMap;
  }, [tree, nodeIds]);
}

/**
 * Gets the progress color based on percentage
 */
export function getProgressColor(percentage: number, isDarkMode: boolean = false): string {
  if (percentage === 0) {
    return isDarkMode ? '#404040' : '#e9ecef';
  }

  if (percentage < 25) {
    return isDarkMode ? '#ff6b6b' : '#dc3545';
  }

  if (percentage < 50) {
    return isDarkMode ? '#ffd43b' : '#ffc107';
  }

  if (percentage < 75) {
    return isDarkMode ? '#ff8787' : '#fd7e14';
  }

  if (percentage < 100) {
    return isDarkMode ? '#4dabf7' : '#007bff';
  }

  return isDarkMode ? '#51cf66' : '#28a745';
}

/**
 * Creates a gradient for the progress bar
 */
export function getProgressGradient(percentage: number, isDarkMode: boolean = false): string {
  const color = getProgressColor(percentage, isDarkMode);
  const lightColor = isDarkMode
    ? lightenColor(color, 0.2)
    : darkenColor(color, 0.1);

  return `linear-gradient(90deg, ${lightColor} 0%, ${color} 100%)`;
}

function lightenColor(color: string, amount: number): string {
  const hex = color.replace('#', '');
  const r = Math.min(255, parseInt(hex.substr(0, 2), 16) + amount * 255);
  const g = Math.min(255, parseInt(hex.substr(2, 2), 16) + amount * 255);
  const b = Math.min(255, parseInt(hex.substr(4, 2), 16) + amount * 255);

  return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
}

function darkenColor(color: string, amount: number): string {
  const hex = color.replace('#', '');
  const r = Math.max(0, parseInt(hex.substr(0, 2), 16) - amount * 255);
  const g = Math.max(0, parseInt(hex.substr(2, 2), 16) - amount * 255);
  const b = Math.max(0, parseInt(hex.substr(4, 2), 16) - amount * 255);

  return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
}